import { memo } from 'react'
import { ISO_DATE_FORMAT, formatApiDate } from '~/shared/lib/date'
import type { FC } from 'react'
import { Box, InputAdornment } from '@mui/material'
import { useSelector } from 'react-redux'

import { DateAndRelativeDateInput, FFInput, PriorityToggle } from '~/shared/ui'
import {
  useGroupOptions,
  UserGroupSelect,
  type AssigneeGroupListItem,
} from '~/entities/assignee-group'
import type { TaskFormDraft } from './taskTypes'
import type { PatientUser } from '~/shared/types'

interface TaskEditorProps {
  task: TaskFormDraft
  onChange: (task: TaskFormDraft) => void
  disabled?: boolean
  patient: PatientUser
  canAssignToPatient?: boolean
  autoFocus?: boolean
}

/**
 * Task draft editor (title with counter, relative-date shorthand, date,
 * assignee, priority) laid out as a fixed-height field row so every control
 * sits flush — heights come from the row, not from each control's internal
 * padding. The assignee uses UserGroupSelect's detailed (avatar autocomplete)
 * mode, matching the case header's control. Used by the To Dos and Inbox task
 * flows; the form-attached inline flow uses InlineTaskEditor.
 */
const TaskEditor: FC<TaskEditorProps> = props => {
  const { task, disabled, patient, autoFocus } = props
  const providers = useSelector(state => state.providers)
  const assigneeGroups = useSelector(state => state.assigneeGroups)
  let isTaskAssigneeGroupProvider: AssigneeGroupListItem | undefined = providers.find(
    provider => provider.assignee_group?.id === task.owner_group,
  )?.assignee_group
  isTaskAssigneeGroupProvider ??= assigneeGroups.find(group => group.id === task.owner_group)

  const enablePriorityToggle = isTaskAssigneeGroupProvider

  const { selectedCaseOwnerGroupOption, flatOptions } = useGroupOptions(
    task.owner_group ?? null,
    !!props.canAssignToPatient,
    patient,
    patient.person.care_team,
    disabled,
  )

  return (
    <Box
      sx={theme => ({
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        // One skin for every field in the editor: bordered white boxes that
        // stretch to their cell, so all controls share the row's height.
        '& .MuiInputBase-root': {
          height: '100%',
          minHeight: '4rem',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: `${theme.shape.borderRadius}px`,
          padding: '0 10px',
          fontSize: '1.3rem',
          alignItems: 'center',
        },
        '& .MuiInputBase-root:hover:not(.Mui-focused)': {
          borderColor: theme.palette.primary.light,
        },
        '& .MuiInputBase-root.Mui-focused': {
          borderColor: theme.palette.primary.main,
        },
        // Standard-variant underlines and outlined notches are replaced by the
        // border above.
        '& .MuiInputBase-root:before, & .MuiInputBase-root:after': { display: 'none' },
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        // Stretch the assignee autocomplete's wrapper chain so its input can
        // take the row height (grid cell -> root -> renderInput Box -> control).
        '& .MuiAutocomplete-root': { height: '100%' },
        '& .MuiAutocomplete-root .MuiBox-root': { height: '100%' },
        '& .MuiFormControl-root': { height: '100%' },
      })}
    >
      <FFInput
        placeholder="Task Title"
        value={task.title}
        fullWidth
        multiline
        onChange={e => props.onChange({ ...props.task, title: e.target.value })}
        disabled={disabled}
        slotProps={{
          htmlInput: {
            maxLength: 255,
          },
          input: {
            autoFocus,
            endAdornment: (
              <InputAdornment position="end">{`${task.title.length}/255`}</InputAdornment>
            ),
          },
        }}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(7rem, 0.4fr) minmax(0, 1fr) minmax(0, 1.4fr) auto',
          gap: 1,
          alignItems: 'stretch',
          height: '4rem',
        }}
      >
        {/* Renders two inputs (relative offset + date) as fragment children,
            which the grid places as its first two cells. */}
        <DateAndRelativeDateInput
          value={formatApiDate(task.due_date, ISO_DATE_FORMAT) ?? ''}
          onChange={due_date => props.onChange({ ...props.task, due_date })}
          disabled={disabled}
        />
        <UserGroupSelect
          selectedValue={selectedCaseOwnerGroupOption ?? null}
          options={flatOptions}
          onChange={ownerGroupOption => {
            const owner_group = ownerGroupOption.value
            if (owner_group) props.onChange({ ...props.task, owner_group })
          }}
          detailed
          fullWidth
          patient={patient}
          disabled={props.disabled}
        />
        {enablePriorityToggle ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PriorityToggle
              priority={task.priority}
              onChange={priority => props.onChange({ ...props.task, priority })}
            />
          </Box>
        ) : (
          <span />
        )}
      </Box>
    </Box>
  )
}

export default memo(TaskEditor)
